import { useState } from "react";
import {
  createPickerSession,
  deletePickerSession,
  importPickedMedia,
  listPickedMedia,
  pollPickerSession,
  requestGooglePhotosToken,
  shouldReconnectPhotos,
  shouldStopImport,
  type GooglePhotosToken,
} from "./google-photos";
import type { MediaItem } from "./media";

interface GooglePhotosImporterProps {
  onImported: (items: MediaItem[]) => void;
}

function displayFilename(filename: string): string {
  return filename.length > 120 ? `${filename.slice(0, 119)}…` : filename;
}

export function GooglePhotosImporter({ onImported }: GooglePhotosImporterProps) {
  const [token, setToken] = useState<GooglePhotosToken | null>(null);
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState("");

  async function connect() {
    setWorking(true);
    setStatus("Connecting to Google Photos…");
    try {
      setToken(await requestGooglePhotosToken());
      setStatus("Connected. Choose up to 50 photos or videos.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not connect to Google Photos.");
    } finally {
      setWorking(false);
    }
  }

  async function chooseAndImport() {
    if (!token) return;
    if (Date.now() >= token.expiresAt) {
      setToken(null);
      setStatus("Google Photos connection expired. Connect again.");
      return;
    }

    const pickerWindow = window.open("about:blank", "_blank", "width=1000,height=720");
    if (!pickerWindow) {
      setStatus("Allow pop-ups for Cat Network, then try again.");
      return;
    }
    setWorking(true);
    setStatus("Opening Google Photos…");
    let sessionId: string | null = null;

    try {
      const session = await createPickerSession(token.accessToken);
      sessionId = session.id;
      pickerWindow.location.href = `${session.pickerUri.replace(/\/$/, "")}/autoclose`;
      setStatus("Choose photos or videos in the Google Photos window.");

      await pollPickerSession(token.accessToken, session);
      const picked = await listPickedMedia(token.accessToken, session.id);
      if (picked.length === 0) {
        setStatus("No media selected.");
        return;
      }

      const imported: MediaItem[] = [];
      let duplicates = 0;
      const failures: string[] = [];
      let remaining = 0;
      for (let index = 0; index < picked.length; index += 1) {
        setStatus(`Importing ${index + 1} of ${picked.length}…`);
        try {
          const result = await importPickedMedia(picked[index], token.accessToken);
          if (result.skipped) duplicates += 1;
          else imported.push(result);
        } catch (error) {
          const filename = displayFilename(picked[index].mediaFile?.filename ?? `Item ${index + 1}`);
          const message = error instanceof Error ? error.message : "Import failed";
          failures.push(`${filename}: ${message}`);
          if (shouldStopImport(error)) {
            if (shouldReconnectPhotos(error)) setToken(null);
            remaining = picked.length - index - 1;
            break;
          }
        }
      }

      onImported(imported);
      const summary = [`${imported.length} new`];
      if (duplicates) summary.push(`${duplicates} already imported`);
      if (failures.length) summary.push(`${failures.length} failed`);
      const stopped = remaining
        ? `${remaining} remaining ${remaining === 1 ? "item was" : "items were"} not attempted.`
        : "";
      const detailParts = [...failures, ...(stopped ? [stopped] : [])];
      const details = detailParts.length ? ` Details: ${detailParts.join(" | ")}` : "";
      setStatus(`Import complete: ${summary.join(", ")}.${details}`);
    } catch (error) {
      if (shouldReconnectPhotos(error)) setToken(null);
      setStatus(error instanceof Error ? error.message : "Google Photos import failed.");
    } finally {
      pickerWindow.close();
      if (sessionId) await deletePickerSession(token.accessToken, sessionId).catch(() => undefined);
      setWorking(false);
    }
  }

  return (
    <section className="google-photos-import" aria-labelledby="google-photos-heading">
      <div>
        <p className="eyebrow">Google Photos</p>
        <h3 id="google-photos-heading">Bring in a memory</h3>
        <p>Select up to 50 photos or videos. Cat Network copies them into the private library.</p>
      </div>
      {!token ? (
        <button type="button" onClick={() => void connect()} disabled={working}>Connect Google Photos</button>
      ) : (
        <button type="button" onClick={() => void chooseAndImport()} disabled={working}>{working ? "Importing…" : "Choose from Google Photos"}</button>
      )}
      {status && <p className="google-photos-status" role="status">{status}</p>}
    </section>
  );
}
